import { ShopwareProduct, ShopwareUuid } from '@services/types/Shopware';
import { CartApiResponse } from '@pages/api/cart/add-bulk-items';

type NewsletterSourcesEnum = 'order' | 'event' | 'footer' | 'popup' | 'start';

interface SubscribeParams {
    email: string;
    country: string;
    store: ShopwareUuid;
    source: NewsletterSourcesEnum;
    tags?: string|null|undefined;
}

class StorefrontApiService {
    basePath: string;

    constructor() {
        this.basePath = '';
    }

    getHeaders() {
        return {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
        };
    }

    apiPath(path: string) {
        return `${this.basePath}/api/${path}`;
    }

    async get(path: string) {
        let response = await fetch(this.apiPath(path), {
            method: 'GET',
            headers: this.getHeaders(),
            credentials: 'include',
        });

        if (response.status >= 400) {
            return { status: 'error', data: null, response: response };
        }

        return await response.json();
    }

    async delete(path: string) {
        let response = await fetch(this.apiPath(path), {
            method: 'DELETE',
            headers: this.getHeaders(),
            credentials: 'include',
        });

        if (response.status >= 400) {
            return { status: 'error', data: null, response: response };
        }

        return response;
    }

    async post(path: string, data: any) {
        let response = await fetch(this.apiPath(path), {
            method: 'POST',
            headers: this.getHeaders(),
            credentials: 'include',
            body: JSON.stringify(data),
        });

        if (response.status >= 400) {
            return { status: 'error', data: null, response: response };
        }

        return await response.json();
    }

    async getProductsElevated(productIds: Array<string>) {
        return this.get(`product/elevated?ids=${productIds.join(',')}`);
    }

    async getProductBySkus(skus: number[]) {
        return this.get(`product/get/by-skus?skus=${skus.join(',')}`);
    }

    async getProductById(productId: string) {
        return this.get(`product/get/by-id/${productId}`);
    }

    async getProductByIds(productIds: Array<string>): Promise<{ products: ShopwareProduct[], status: string }> {
        // @ts-ignore
        return this.get(`product/get/by-ids?ids=${productIds.map((p) => p.replaceAll('-', '')).join(',')}`);
    }

    async fetchStaticLabels() {
        return this.get('properties/labels');
    }

    async getPhysicalStores() {
        return {
            'e44cddf14cdc5b008b499dce5017d1d8': {
                propertyId: 'e44cddf14cdc5b008b499dce5017d1d8',
                code: 'NKPG',
                storeId: '',
                name: 'Norrköping, Linden',
                address: 'Drottninggatan 41<br/>602 32 Norrköping',
                phone: '011 - 16 00 09',
                email: 'norrkoping@newport.se',
                openingHours: 'Mån - fre 10.00 - 19.00<br/>Lör 10.00 - 17.00<br/>Sön 12.00 - 16.00'
            },
            'dc714866141650f18fa649bd0e9214df': {
                propertyId: 'dc714866141650f18fa649bd0e9214df',
                code: 'BRM',
                storeId: '',
                name: 'Stockholm, Bromma Blocks',
                address: 'Ulvsundavägen 185–193<br/>168 67 Bromma<br/>',
                phone: '08 - 29 59 88',
                email: 'bromma@newport.se',
                openingHours: 'Mån - fre 10.00 - 20.00<br/>Lör 10.00 - 18.00<br/>Sön 10.00 - 18.00'
            },
            'f9fe489339a857fb8f2888bc736f8724': {
                propertyId: 'f9fe489339a857fb8f2888bc736f8724',
                code: 'LKPG',
                storeId: '',
                name: 'Linköping, Gränden',
                address: 'Nygatan 22<br/>582 19 Linköping<br/>',
                phone: '013 - 913 00 13',
                email: 'linkoping@newport.se',
                openingHours: 'Mån - fre 10.00 - 19.00<br/>Lör 10.00 - 17.00<br/>Sön 12.00 - 16.00'
            },
            '5934e84063b7552ba4c7cd30b1c65173': {
                propertyId: '5934e84063b7552ba4c7cd30b1c65173',
                code: 'NK',
                storeId: '',
                name: 'Stockholm, NK',
                address: 'NK Hamngatan 18-20<br/>111 47 Stockholm<br/>',
                phone: '08 - 762 81 60',
                email: 'nk@newport.se',
                openingHours: 'Mån - fre 10.00 - 19.00<br/>Lör 10.00 - 18.00<br/>Sön 11.00 - 17.00'
            },
            '665c24a31b7f55b0acd210078336026a': {
                propertyId: '665c24a31b7f55b0acd210078336026a',
                code: 'VAS',
                storeId: '',
                name: 'Västerås, Punkt Galleria',
                address: 'Stora Gatan 7-21<br/>722 12 Västerås<br/>',
                phone: '021 - 421 00 60',
                email: 'vasteras@newport.se',
                openingHours: 'Mån - fre 10.00 - 19.00<br/>Lör 10.00 - 17.00<br/>Sön 11.00 - 16.00'
            },
            '74f82fd38ae55602856aeefee7b73a96': {
                propertyId: '74f82fd38ae55602856aeefee7b73a96',
                code: 'ARE',
                storeId: '',
                name: 'Åre, By',
                address: 'Årevägen 128<br/>837 52 Åre<br/>',
                phone: '0647 - 67 50 30',
                email: 'are@newport.se',
                openingHours: 'Mån - fre 11.00 - 17.00<br/>Lör 11.00 - 15.00<br/>Sön Stängt'
            },
            '40ac0e0599685fe4b0188b2c57def167': {
                propertyId: '40ac0e0599685fe4b0188b2c57def167',
                code: 'SSB',
                storeId: '',
                name: 'Steninge slottsby',
                address: 'Steninge Slottsväg 141<br/>195 59 Märsta<br/>',
                phone: '08 - 502 573 90',
                email: 'steninge@newport.se',
                openingHours: 'Mån - fre 11.00 - 18.00<br/>Lör 10.00 - 17.00<br/>Sön 10.00 - 17.00'

            },
            'b1875d03d8175df08d1d3682e77f0f66': {
                propertyId: 'b1875d03d8175df08d1d3682e77f0f66',
                code: 'NKPGOUT',
                storeId: '',
                name: 'Norrköping Outlet, Ingelsta',
                address: 'Kromgatan 2<br/>602 23 Norrköping<br/>',
                phone: '011 - 443 39 90',
                email: 'outlet.norrkoping@newport.se',
                openingHours: 'Mån - fre 10.00 - 19.00<br/>Lör 10.00 - 17.00<br/>Sön 11.00 - 17.00'
            },
            'cd2e8cacf7c258be8382220fc36e089c': {
                propertyId: 'cd2e8cacf7c258be8382220fc36e089c',
                code: 'NKGBG',
                storeId: '',
                name: 'Göteborg, NK',
                address: 'Östra Hamngatan 42<br/>411 09 Göteborg<br/>',
                phone: '031 - 710 10 09',
                email: 'goteborg@newport.se',
                openingHours: 'Mån - fre 10.00 - 19.00<br/>Lör 10.00 - 18.00<br/>Sön 11.00 - 16.00'
            },
            '91b1c68ed4c65463983355cc9abe0653': {
                propertyId: '91b1c68ed4c65463983355cc9abe0653',
                code: 'NGA',
                storeId: '',
                name: 'Stockholm, Newport Gallery',
                address: 'Regeringsgatan 48<br/>111 56 Stockholm<br/>',
                phone: '08 - 50 25 73 95',
                email: 'gallery@newport.se',
                openingHours: 'Mån - fre 10.00 - 19.00<br/>Lör 10.00 - 18.00<br/>Sön 11.00 - 17.00'
            }
        }
    }

    async getVariants(parentId: string) {
        return this.get(`product/get/variants/${parentId}`);
    }

    async addItemsToCart(productIds: Array<string>): Promise<CartApiResponse> {
        return this.post('cart/add-items', {
            productIds,
        });
    }

    async addProductsToCart(products: Array<{ productId: ShopwareUuid, quantity: number }>): Promise<CartApiResponse> {
        return this.post('cart/add-bulk-items', {
            products,
        });
    }

    async subscribeToNewsletter(payload: SubscribeParams) {
        return this.post('voyado/newsletter', payload);
    }
}

export default StorefrontApiService;
